import type React from "react";
import { useState } from "react";
import { Card } from "../../misc/Card";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import { Button } from "../../misc/Button";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "./InstagramCarousel";
import type { InstagramPostProps } from "../types";
import {
    Bookmark,
    ChevronLeft,
    ChevronRight,
    Heart,
    Loader,
    MessageCircle,
    Send,
} from "lucide-react";
import { MediaType } from "../../../types/media";
import useProfileImage from "../../../hooks/useProfileImage";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { calculateFittedFontSize } from "../../../utils/autoFitFont";

const InstagramPost: React.FC<InstagramPostProps> = ({ post, setLoading, hideDatetime = false, contentCharLimit }) => {
    const isMobile = useIsMobile(900);

    const user = useSelector((state: RootState) => state.auth.user);
    const profileImage = useProfileImage(user);

    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);

    const toggleCaption = () => {
        setIsCaptionExpanded((prev) => !prev);
    };

    // Handle slide change from Carousel
    const handleSlideChange = (index: number) => {
        setCurrentMediaIndex(index);
    };

    // Render the caption text
    const renderCaption = () => {
        const rawContent = (
            Array.isArray(post.content)
                ? post.content.join("\n\n")
                : post.content || ""
        ).trim();
        const withinContent = contentCharLimit ? rawContent.substring(0, contentCharLimit) : rawContent;
        const overflowContent = contentCharLimit && rawContent.length > contentCharLimit ? rawContent.substring(contentCharLimit) : "";
        const content = withinContent;
        const CHARACTER_LIMIT = 125;
        const getContentParagraphs = (text: string) =>
            text.split(/\n+/).map((para, index) => (
                <p key={index} className="mb-2">
                    {para}
                </p>
            ));
        const isContentTruncated = content.length > CHARACTER_LIMIT;
        const overflowBlock = overflowContent ? (
            <span className="bg-red-100 line-through text-red-600">{overflowContent}</span>
        ) : null;

        if (!isContentTruncated) {
            return (
                <>
                    {getContentParagraphs(content)}
                    {overflowBlock}
                    {post.footerType === "in-post" && !isCaptionExpanded && (
                        <button
                            onClick={toggleCaption}
                            className="text-sm text-blue-500 ml-1"
                            aria-label="Show more"
                        >
                            more
                        </button>
                    )}
                    {post.footerType === "in-post" &&
                        isCaptionExpanded &&
                        post.footer && (
                            <>
                                <div className="mt-2 break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                    {post.footer
                                        .split(/\n+/)
                                        .map((para, index) => (
                                            <p key={index}>{para}</p>
                                        ))}
                                </div>
                                <button
                                    onClick={toggleCaption}
                                    className="text-sm text-blue-500 ml-1"
                                    aria-label="Show less"
                                >
                                    less
                                </button>
                            </>
                        )}
                </>
            );
        }

        if (isCaptionExpanded) {
            return (
                <>
                    {getContentParagraphs(content)}
                    {overflowBlock}
                    {post.footerType === "in-post" && post.footer && (
                        <div className="mt-2 break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            {post.footer.split(/\n+/).map((para, index) => (
                                <p key={index}>{para}</p>
                            ))}
                        </div>
                    )}
                    <button
                        onClick={toggleCaption}
                        className="text-sm text-blue-500 ml-1"
                        aria-label="Show less"
                    >
                        less
                    </button>
                </>
            );
        } else {
            let truncatedContent = content.slice(0, CHARACTER_LIMIT);
            const lastSpace = truncatedContent.lastIndexOf(" ");
            if (lastSpace > 0) {
                truncatedContent = truncatedContent.slice(0, lastSpace);
            }
            return (
                <>
                    {getContentParagraphs(truncatedContent)}
                    <button
                        onClick={toggleCaption}
                        className="text-sm text-blue-500 ml-1"
                        aria-label="Read more"
                    >
                        more
                    </button>
                </>
            );
        }
    };

    // Calculate time display - for preview mode, show formatted date/time
    let time: string;
    
    // Check if this is a Lead Generation post (for media rendering logic)
    const isLeadGenerationPost = post.type === 'Lead Generation' || 
                                (post.type === 'preview' && post.background && post.textColor);
    
    if (hideDatetime) {
        time = "";
    } else if (post.type === 'preview') {
        // Show formatted date/time in preview mode (for ALL post types including Lead Generation)
        const date = new Date(post.scheduledFor);
        const dateString = date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
        });
        const timeString = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        time = `${dateString} at ${timeString}`;
    } else {
        // Show relative time for scheduled/published posts (for ALL post types including Lead Generation)
        const diffMs = Date.now() - new Date(post.scheduledFor).getTime();
        const diffMin = Math.floor(Math.abs(diffMs) / (1000 * 60));
        if (diffMs >= 0) {
            // Post time is in the past
            if (diffMin < 60) {
                time = `${diffMin} m`;
            } else {
                const diffHr = Math.floor(diffMin / 60);
                if (diffHr < 24) {
                    time = `${diffHr} hr`;
                } else {
                    const diffDays = Math.floor(diffHr / 24);
                    time = `${diffDays} d`;
                }
            }
        } else {
            // Post time is in the future
            if (diffMin < 60) {
                time = `in ${diffMin} m`;
            } else if (diffMin < 1440) {
                const diffHr = Math.floor(diffMin / 60);
                time = `in ${diffHr} hr`;
            } else {
                const diffDays = Math.floor(diffMin / 1440);
                time = `in ${diffDays} d`;
            }
        }
    }

    const mediaItems = Array.isArray(post.media)
        ? post.media
        : post.media
        ? [post.media]
        : [];


    return (
        <Card className="max-w-[485px] mx-auto bg-white shadow-sm rounded-lg overflow-hidden border border-gray-300">
            {/* Post Header */}
            <div className="p-4 flex items-center justify-between border-b">
                <div className="flex items-center gap-3">
                    {(post?.userImage && post.userImage.trim() !== "") ||
                    (user?.profileImage && user.profileImage.trim() !== "") ||
                    (user?.photoURL && user.photoURL.trim() !== "") ? (
                        <img
                            src={
                                post?.userImage && post.userImage.trim() !== ""
                                    ? post.userImage
                                    : profileImage || undefined
                            }
                            width={40}
                            height={40}
                            alt="Profile"
                            className="rounded-full object-cover"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <span 
                        className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                        style={{ display: (post?.userImage && post.userImage.trim() !== "") || (user?.profileImage && user.profileImage.trim() !== "") || (user?.photoURL && user.photoURL.trim() !== "") ? 'none' : 'flex' }}
                    >
                        {user && user.nameFirst && user.nameLast
                            ? user.nameFirst[0].toUpperCase() + user.nameLast[0].toUpperCase()
                            : post?.displayName 
                            ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'U'}
                    </span>
                    <div className="flex flex-row items-center gap-2">
                        <span className="font-semibold text-sm hover:underline cursor-pointer">
                            {post?.username && post?.profileUrl ? (
                                <a
                                    href={post.profileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold hover:underline cursor-pointer"
                                >
                                    {post.username}
                                </a>
                            ) : (
                                `${user?.nameFirst} ${user?.nameLast}`
                            )}
                        </span>
                        {/* Timestamp */}
                        {!hideDatetime && (
                            <p className="text-xs text-gray-500 uppercase">
                                {time}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Media Carousel */}
            <div
                className="relative w-full bg-black overflow-hidden"
                id={`carousel-${post.id}`}
            >
                {/* For Lead Generation posts: if image is generated, display it instead of re-rendering styled block */}
                {isLeadGenerationPost && mediaItems && mediaItems.length > 0 && mediaItems[0].signedUrl ? (
                    <div
                        className="w-full flex items-center justify-center"
                        style={isMobile ? { aspectRatio: "1/1" } : {}}
                    >
                        <img
                            src={mediaItems[0].signedUrl}
                            alt="Lead Generation Post"
                            className="object-contain w-full h-full"
                        />
                    </div>
                ) : post.background && post.textColor ? (
                    (() => {
                        // Platform-specific dimensions (matching backend)
                        const platformDimensions = { width: 1080, height: 1080 }; // Instagram square
                        const maxWidth = 485; // Instagram post max width
                        const scale = maxWidth / platformDimensions.width;
                        const displaySize = platformDimensions.width * scale;

                        // Use imageText for the graphic if available, otherwise use content
                        const graphicText = (post as any).imageText || post.content;
                        const igContent = typeof graphicText === "string"
                            ? graphicText
                            : Array.isArray(graphicText)
                            ? graphicText.join("\n\n")
                            : "";
                        const baseFontSize = post.fontSize || 20;

                        if (isMobile) {
                            // Mobile: use fluid layout — no fixed px dimensions, let it fill the container
                            // Estimate a conservative mobile width (320px) for font-size fitting
                            const mobileFontSize = calculateFittedFontSize(igContent, baseFontSize, post.fontFamily, 320, 320);
                            return (
                                <div
                                    className="w-full flex items-center justify-center"
                                    style={{
                                        aspectRatio: "1/1",
                                        background: (() => {
                                            const bg = post.background;
                                            if (!bg) return undefined;
                                            if (bg.type === 'gradient') {
                                                return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                                            }
                                            return bg.color || '#000000';
                                        })(),
                                        color: post.textColor,
                                        fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                                        padding: "8%",
                                    }}
                                >
                                    <p
                                        className="font-bold text-center break-words"
                                        style={{
                                            fontSize: `${mobileFontSize}px`,
                                            lineHeight: 1.5,
                                            wordBreak: "break-word",
                                            overflowWrap: "anywhere",
                                            width: "100%",
                                        }}
                                    >
                                        {igContent}
                                    </p>
                                </div>
                            );
                        }

                        // Desktop: original pixel-based rendering (unchanged)
                        const fittedFontSize = calculateFittedFontSize(igContent, baseFontSize, post.fontFamily, displaySize, displaySize);
                        const padding = Math.round(displaySize * 0.08);
                        return (
                            <div className="w-full flex items-center justify-center"
                                 style={{ 
                                     background: (() => {
                                         const bg = post.background;
                                         if (!bg) return undefined;
                                         if (bg.type === 'gradient') {
                                             return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                                         }
                                         return bg.color || '#000000';
                                     })(), 
                                     color: post.textColor,
                                     fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                                     width: `${displaySize}px`,
                                     height: `${displaySize}px`,
                                     padding: `${padding}px`,
                                 }}>
                                <p className="font-bold text-center"
                                   style={{
                                       fontSize: `${fittedFontSize}px`,
                                       lineHeight: 1.5,
                                   }}>
                                    {igContent}
                                </p>
                            </div>
                        );
                    })()
                ) : (
                    <Carousel
                        className="w-full h-full"
                        onSlideChange={handleSlideChange}
                    >
                        <CarouselContent>
                        {mediaItems &&
                            mediaItems?.length > 0 &&
                            mediaItems?.map((item, index) => (
                                <CarouselItem key={index}>
                                    <div className="w-full h-full flex items-center justify-center">
                                        {item.type === MediaType.AssetVideo ||
                                        item.type === MediaType.ListingVideo ? (
                                            <div className="w-full aspect-[4/5] bg-black overflow-hidden relative">
                                                {item?.isProcessing ? (
                                                    <>
                                                        {item?.thumbnail && (
                                                            <img
                                                                src={item?.thumbnail}
                                                                alt="Media"
                                                                className="w-full h-full object-contain cursor-pointer"
                                                            />
                                                        )}
                                                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                                                            <span className="text-white text-sm font-medium">
                                                                Video Processing
                                                            </span>
                                                            <Loader className="ml-2 animate-spin text-secondary" />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <video
                                                        muted
                                                        src={item.signedUrl || item.url || ""}
                                                        className="w-full h-full object-contain"
                                                        controls
                                                        autoPlay={isMobile}
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            <img
                                                src={
                                                    item.signedUrl ||
                                                    item.url ||
                                                    ""
                                                }
                                                alt={`Post media ${index + 1}`}
                                                className="max-h-[585px] max-w-full object-contain"
                                            />
                                        )}
                                    </div>
                                </CarouselItem>
                            ))}
                    </CarouselContent>

                    {post.media.length > 1 && (
                        <>
                            <CarouselPrevious>
                                <ChevronLeft />
                            </CarouselPrevious>
                            <CarouselNext>
                                <ChevronRight />
                            </CarouselNext>
                            <div className="absolute top-2 right-2 bg-black/50 text-white rounded-full px-2 py-1 text-xs">
                                {currentMediaIndex + 1} / {post.media.length}
                            </div>
                        </>
                    )}
                </Carousel>
                )}
            </div>

            {/* Action Buttons */}
            <div className="p-4 flex items-center justify-between">
                <div className="flex gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Like"
                        className="p-2 hover:bg-gray-200 rounded-full"
                    >
                        <Heart className="h-6 w-6" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Comment"
                        className="p-2 hover:bg-gray-200 rounded-full"
                    >
                        <MessageCircle className="h-6 w-6" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Share"
                        className="p-2 hover:bg-gray-200 rounded-full"
                    >
                        <Send className="h-6 w-6" />
                    </Button>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Bookmark"
                    className="p-2 hover:bg-gray-200 rounded-full"
                >
                    <Bookmark className="h-6 w-6" />
                </Button>
            </div>

            {/* Likes */}
            <div className="px-4 pb-2">
                <p className="font-semibold text-sm">likes</p>
            </div>

            {/* Caption */}
            {
            // !(post.background && post.textColor) && (
                <div className="px-4 pb-2">
                    {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
                    !post.content &&
                    (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
                        <div className="space-y-2 py-1">
                            <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
                            <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
                        </div>
                    ) : (
                        <p className="text-sm break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            <span className="font-semibold mr-1">{post?.username}</span>
                            {renderCaption()}
                        </p>
                    )}
                </div>
            // )
            }

            {!isLeadGenerationPost && post.footerType === "comment" && post.includeFooter && post.footer && (
                <div className="px-4 pb-2 overflow-hidden">
                    <div className="flex items-start gap-2 overflow-hidden">
                        {(post?.userImage && post.userImage.trim() !== "") ||
                        (user?.profileImage &&
                            user.profileImage.trim() !== "") ||
                        (user?.photoURL && user.photoURL.trim() !== "") ? (
                            <img
                                src={
                                    post?.userImage &&
                                    post.userImage.trim() !== ""
                                        ? post.userImage
                                        : profileImage || '/placeholder-avatar.svg'
                                }
                                width={40}
                                height={40}
                                alt="Profile"
                                className="rounded-full object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallback = target.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <span 
                            className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                            style={{ display: (post?.userImage && post.userImage.trim() !== "") || (user?.profileImage && user.profileImage.trim() !== "") || (user?.photoURL && user.photoURL.trim() !== "") ? 'none' : 'flex' }}
                        >
                            {user && user.nameFirst && user.nameLast
                                ? user.nameFirst[0].toUpperCase() + user.nameLast[0].toUpperCase()
                                : post?.displayName 
                                ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                : 'U'}
                        </span>
                        <div className="min-w-0 overflow-hidden">
                            {/* The comment text: username in bold, then footer text */}
                            <div className="text-sm break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                <span className="font-semibold mr-1">
                                    {post?.username ||
                                        `${user?.nameFirst} ${user?.nameLast}`}
                                </span>
                                {post.footer
                                    .split("\n")
                                    .map((para, index) =>
                                        para ? (
                                            <p key={index}>{para}</p>
                                        ) : (
                                            <p key={index}>&nbsp;</p>
                                        )
                                    )}
                            </div>
                            {/* Timestamp + Reply line (typical IG style) */}
                            <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                {!hideDatetime && <span>{time}</span>}
                                <span>Reply</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default InstagramPost;
